import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { chartOfAccountsService } from '../../services/accounting/chart-of-accounts';
import { XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../ui/LoadingSpinner';
import toast from 'react-hot-toast';

const TrialBalanceModal = ({ onClose }) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);

  const loadTrialBalance = useCallback(async () => {
    if (!currentCompany?.id) return;
    
    setLoading(true);
    try {
      const accountsData = await chartOfAccountsService.getChartOfAccounts(currentCompany.id);
      // Only show accounts with balances or that are actively used
      const activeAccounts = accountsData.filter(account => 
        account.balance !== 0 || account.isActive
      );
      setAccounts(activeAccounts);
    } catch (error) {
      console.error('Error loading trial balance:', error);
      toast.error(t('reports.error_loading_trial_balance', 'Failed to load trial balance'));
    } finally {
      setLoading(false);
    }
  }, [currentCompany?.id, t]);

  useEffect(() => {
    loadTrialBalance();
  }, [loadTrialBalance]);

  const calculateTotals = () => {
    let totalDebits = 0;
    let totalCredits = 0;

    accounts.forEach(account => {
      const balance = account.balance || 0;
      if (balance > 0) {
        // Assets and expenses have debit balances
        if (account.accountType === 'asset' || account.accountType === 'expense') {
          totalDebits += balance;
        } else {
          // Liabilities, equity, and revenue have credit balances
          totalCredits += balance;
        }
      } else if (balance < 0) {
        // Negative balances show on opposite side
        if (account.accountType === 'asset' || account.accountType === 'expense') {
          totalCredits += Math.abs(balance);
        } else {
          totalDebits += Math.abs(balance);
        }
      }
    });

    return { totalDebits, totalCredits };
  };

  const { totalDebits, totalCredits } = calculateTotals();
  const isBalanced = Math.abs(totalDebits - totalCredits) < 0.01;

  const exportToPDF = async () => {
            toast(t('reports.export_coming_soon', 'PDF export coming soon'));
  };

  const getAccountBalance = (account) => {
    const balance = account.balance || 0;
    const debitBalance = 
      ((account.accountType === 'asset' || account.accountType === 'expense') && balance > 0) ||
      ((account.accountType === 'liability' || account.accountType === 'equity' || account.accountType === 'revenue') && balance < 0);
    
    return {
      debit: debitBalance ? Math.abs(balance) : 0,
      credit: !debitBalance ? Math.abs(balance) : 0
    };
  };

  const groupedAccounts = accounts.reduce((groups, account) => {
    const type = account.accountType;
    if (!groups[type]) {
      groups[type] = [];
    }
    groups[type].push(account);
    return groups;
  }, {});

  const accountTypeOrder = ['asset', 'liability', 'equity', 'revenue', 'expense'];
  const accountTypeNames = {
    asset: t('accounting.assets'),
    liability: t('accounting.liabilities'),
    equity: t('accounting.equity'),
    revenue: t('chart.revenue', 'Revenue'),
    expense: t('accounting.expenses')
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t('accounting.trial_balance')}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {t('reports.as_of_date', 'As of')} {new Date(asOfDate).toLocaleDateString()}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="date"
                value={asOfDate}
                onChange={(e) => setAsOfDate(e.target.value)}
                className="input input-bordered input-sm"
              />
              <button
                onClick={exportToPDF}
                className="btn btn-sm btn-outline"
              >
                <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
                {t('accounting.export_pdf')}
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Balance Status */}
              <div className={`p-4 rounded-lg border ${
                isBalanced ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className={`text-lg ${isBalanced ? 'text-green-600' : 'text-red-600'}`}>
                      {isBalanced ? '✅' : '❌'}
                    </span>
                    <span className={`font-medium ${isBalanced ? 'text-green-800' : 'text-red-800'}`}>
                      {isBalanced 
                        ? t('reports.trial_balance_balanced', 'Trial Balance is Balanced')
                        : t('reports.trial_balance_unbalanced', 'Trial Balance is NOT Balanced')
                      }
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">
                    {t('reports.difference', 'Difference')}: SAR {Math.abs(totalDebits - totalCredits).toFixed(2)}
                  </div>
                </div>
              </div>

              {/* Trial Balance Table */}
              <div className="overflow-x-auto border rounded-lg">
                <table className="table table-zebra w-full">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left font-semibold">{t('chart.account_code', 'Account Code')}</th>
                      <th className="text-left font-semibold">{t('chart.account_name', 'Account Name')}</th>
                      <th className="text-right font-semibold">{t('accounting.debit')}</th>
                      <th className="text-right font-semibold">{t('accounting.credit')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accountTypeOrder.map(accountType => {
                      const typeAccounts = groupedAccounts[accountType] || [];
                      if (typeAccounts.length === 0) return null;

                      return (
                        <React.Fragment key={accountType}>
                          {/* Account Type Header */}
                          <tr className="bg-blue-50 border-b-2 border-blue-200">
                            <td colSpan="4" className="font-bold text-blue-900 py-3">
                              {accountTypeNames[accountType]}
                            </td>
                          </tr>
                          
                          {/* Account Rows */}
                          {typeAccounts.map(account => {
                            const { debit, credit } = getAccountBalance(account);
                            return (
                              <tr key={account.id}>
                                <td className="font-mono text-sm">{account.accountCode}</td>
                                <td className="font-medium">{account.accountName}</td>
                                <td className="text-right font-medium">
                                  {debit > 0 ? (
                                    <span className="text-green-600">SAR {debit.toFixed(2)}</span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="text-right font-medium">
                                  {credit > 0 ? (
                                    <span className="text-red-600">SAR {credit.toFixed(2)}</span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 font-bold border-t-2 border-gray-300">
                      <td colSpan="2" className="text-right font-bold text-lg">
                        {t('journal.totals')}
                      </td>
                      <td className="text-right font-bold text-lg text-green-600">
                        SAR {totalDebits.toFixed(2)}
                      </td>
                      <td className="text-right font-bold text-lg text-red-600">
                        SAR {totalCredits.toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Account Type Summaries */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {accountTypeOrder.map(accountType => {
                  const typeAccounts = groupedAccounts[accountType] || [];
                  const typeTotal = typeAccounts.reduce((sum, account) => sum + Math.abs(account.balance || 0), 0);
                  
                  if (typeTotal === 0) return null;

                  return (
                    <div key={accountType} className="card bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200">
                      <div className="card-body text-center p-4">
                        <div className="font-semibold text-blue-900 text-sm mb-1">
                          {accountTypeNames[accountType]}
                        </div>
                        <div className="text-lg font-bold text-blue-600">
                          SAR {typeTotal.toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {typeAccounts.length} {t('reports.accounts', 'accounts')}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              {t('pdf.generated_on')} {new Date().toLocaleString()}
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-outline"
              >
                {t('common.close', t('common.close'))}
              </button>
              <button
                type="button"
                onClick={loadTrialBalance}
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  '🔄'
                )}
                {t('common.refresh')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrialBalanceModal; 